import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import API from '../../../API';
import Item from './Item';
import ProgressOverlay from '../../overlays/ProgressOverlay';
import ShippingNotice from '../ShippingNotice';
import { useTenantPath } from '../../../utils/tenantPath';
import { selectVisibleNewArrivals } from '../../../utils/newArrivals';

// How many items to ask the server for. The route /items/recent caps at
// 50 today; this is sized to match. Operators rarely land more than a
// few dozen items in a week, so 50 is comfortable headroom for the
// "show everything within 7 days of the newest" rule.
const FETCH_LIMIT = 50;

const NewArrivalsPanel = () => {
  const { store } = useParams<{ store: string }>();
  const path = useTenantPath();
  const [loading, setLoading] = useState<boolean>(true);
  const [items, setItems] = useState<IItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { items: fetched } = await API.getRecentItems(store, FETCH_LIMIT);
        if (cancelled) return;
        setItems(selectVisibleNewArrivals(fetched));
      } catch (err: any) {
        console.error('NewArrivalsPanel fetch failed:', err?.message ?? err);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [store]);

  return (
    <ProgressOverlay enabled={loading}>
      <div className="infobar">
        <div className="breadcrumb">
          &nbsp;&nbsp; <span><Link to={path.category()}>Categories</Link></span>
          &nbsp;&nbsp; &rsaquo;
          &nbsp;&nbsp; <span>New Arrivals</span>
        </div>
        <div className="spacer" />
        <ShippingNotice />
      </div>
      {!loading && items.length === 0 && (
        <Typography
          variant="body2"
          sx={{ padding: '2rem 1rem', textAlign: 'center', color: '#64748b' }}
        >
          No new arrivals yet — check back soon.
        </Typography>
      )}
      <Grid container justifyContent="center" spacing={2} className="items">
        {items.map((item) => (
          <Grid key={item.id} item>
            <Item item={item} />
          </Grid>
        ))}
      </Grid>
    </ProgressOverlay>
  );
};

export default NewArrivalsPanel;
